import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Icon } from "react-icons-kit";
import { ic_arrow_back, ic_delete_outline } from "react-icons-kit/md";
import { Card, Table, Form, Row, Col, Modal, Button } from "react-bootstrap";
import MainWrapper from "../../MainWrapper";
import { toast } from "react-toastify";
import { CNC_ORDER_DETAIL_REQUEST, CNC_CUSTOMER_INFO_REQUEST, CNC_ORDER_DELETE_REQUEST, CNC_ORDER_UPDATE_REQUEST, CNC_ORDER_CONFIRM_REQUEST } from "../../../_utils/constants/Customer";
import { recalcLinePrice, syncKitAddonQuantity, syncDerivedFromEaAddon } from "../../../_utils/manualEstimateItems";
import { backfillKitParentLabels } from "../../../_utils/manualEstimatePayload";
import { getOrderLineStructureName } from "../../../_utils/countertopOrderItems";
import { stripRouteParamKey } from "./cncOrderDetailHelpers";
import { PRODUCT_TYPE_REQUEST } from "../../../_utils/constants/ProductCatalog";
import AddScopeModal from "../customer/models/AddScopeModal";
import AddLaborItemModal from "../customer/models/AddLaborItemModal";

const fmt = (n) => {
  const num = parseFloat(n ?? 0);
  return isNaN(num) ? "0.00" : num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const calcSection = (items = []) => {
  let cost = 0, total = 0;
  items.forEach(x => {
    const lp = parseFloat(x.linePrice ?? 0);
    const gpm = parseFloat(x.gpm ?? 0);
    total += lp;
    cost += lp * (100 - gpm) / 100;
  });
  const profit = total - cost;
  const gpm = total > 0 ? (profit / total) * 100 : 0;
  return { cost, profit, gpm, total };
};

// Normalize raw order items from the server into the editable local shape.
const mapOrderItems = (src) => {
  const raw = (src?.result ?? src)?.orderItems ?? [];
  return raw.map(item => {
    const material = item.material ?? item.materials ?? item.materialItems ?? [];
    const labor = item.labor ?? item.labors ?? item.laborItems ?? [];
    // Label each kit row with the primary it belongs to (same as the manual-estimate grid), so it's
    // clear which kit item goes with which primary — derived when the order carries the linkage.
    const labeled = backfillKitParentLabels(material, labor);
    return {
      ...item,
      productStructureId: item.ProductStructureId ?? item.productStructureId,
      material: labeled.materialList,
      labor: labeled.laborList,
    };
  });
};

const CncOrderDetail = ({
  auth: { auth, isAuthenticated },
  customerInfo: { cncOrderDetail, cncCustomerInfo, cncOrderDeleted, cncOrderUpdated, cncOrderConfirmed, isLoading },
  fetchCncOrderDetail,
  fetchCncCustomerInfo,
  deleteCncOrder,
  updateCncOrder,
  confirmCncOrder,
  ProductType,
  fetchProductType,
}) => {
  // The route also matches /cnc-estimate/contactInfoId=1/unitId=2/jobNum=CC-1, where React Router v6
  // captures whole segments ("jobNum=CC-1") — strip the "key=" prefix (same as OrderConfirmation).
  const routeParams = useParams();
  const jobNum = stripRouteParamKey(routeParams.jobNum, "jobNum");
  const contactInfoId = stripRouteParamKey(routeParams.contactInfoId, "contactInfoId");
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [measureDate, setMeasureDate] = useState("");
  const [pdfFile, setPdfFile] = useState(null);
  const [editErrors, setEditErrors] = useState({});
  const [localOrderItems, setLocalOrderItems] = useState([]);
  const [addScopeOpen, setAddScopeOpen] = useState(false);
  // Per-scope "Add New Material/Labor Item" — the orderItem index each modal targets (null = closed).
  const [addMaterialTargetIdx, setAddMaterialTargetIdx] = useState(null);
  const [addLaborTargetIdx, setAddLaborTargetIdx] = useState(null);
  const confirmedOnMount = useRef(cncOrderConfirmed);
  const token = isAuthenticated && auth?.[0]?.token;
  const hasProductTypes = Boolean(ProductType?.valueList?.length);
  const structureNames = {};
  (ProductType?.valueList || []).forEach((type) => {
    structureNames[type.productStructureId] = type.productStructure;
  });

  useEffect(() => {
    if (token && !hasProductTypes) fetchProductType(token);
  }, [token, hasProductTypes, fetchProductType]);

  useEffect(() => {
    if (!isAuthenticated) navigate("/login");
  }, [auth, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && jobNum) {
      fetchCncOrderDetail(auth[0].token, jobNum);
    }
  }, [auth, isAuthenticated, jobNum]);

  useEffect(() => {
    if (isAuthenticated && contactInfoId) {
      fetchCncCustomerInfo(auth[0].token, auth[0].userId, contactInfoId);
    }
  }, [auth, isAuthenticated, contactInfoId]);

  useEffect(() => {
    if (cncOrderDeleted) navigate("/cnc-orders");
  }, [cncOrderDeleted]);

  useEffect(() => {
    setLocalOrderItems(mapOrderItems(cncOrderDetail));
  }, [cncOrderDetail]);

  useEffect(() => {
    if (cncOrderUpdated) {
      toast.success("Order updated successfully.");
      setIsEditMode(false);
      setMeasureDate("");
      setPdfFile(null);
      fetchCncOrderDetail(auth[0].token, jobNum);
    }
  }, [cncOrderUpdated]);

  useEffect(() => {
    if (cncOrderConfirmed && !confirmedOnMount.current) {
      toast.success("Order confirmed successfully.");
      navigate("/cnc-orders");
    }
  }, [cncOrderConfirmed]);

  const d = cncOrderDetail?.result ?? cncOrderDetail;
  const c = cncCustomerInfo;
  const firstContact = c?.ilContactViewModel?.[0];
  const isReadOnlyView = localStorage.getItem('Existing_Orders') === 'true' || Number(d?.isCCConfirm ?? d?.IsCCConfirm) === 1;
  const orderItems = d?.orderItems ?? [];
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

  const sumLinePrices = (items = []) => items.reduce((sum, item) => {
    const mats = item.material ?? item.materials ?? item.materialItems ?? [];
    const labs = item.labor ?? item.labors ?? item.laborItems ?? [];
    return sum + [...mats, ...labs].reduce((s, x) => s + parseFloat(x.linePrice ?? 0), 0);
  }, 0);

  // In edit mode, recompute live from the local items so deletes / qty edits are reflected.
  const estimateTotal = isEditMode
    ? sumLinePrices(localOrderItems)
    : (d?.estimateTotal ?? sumLinePrices(orderItems));

  const clampQty = (v) =>
    String(v).trim() !== "" && Number(v) < 0 ? "0" : v;

  const applyQtyToItem = (item, section, lineIdx, val, syncKit) => {
    const mats = item.material ?? [];
    const labs = item.labor ?? [];

    if (section === "labor") {
      return {
        ...item,
        labor: labs.map((l, j) =>
          j === lineIdx ? recalcLinePrice({ ...l, quantity: val }) : l
        ),
      };
    }

    // Material path
    const updatedMats = mats.map((m, j) =>
      j === lineIdx ? recalcLinePrice({ ...m, quantity: val }) : m
    );
    const current = mats[lineIdx];

    if (!syncKit) return { ...item, material: updatedMats };

    if (current?.isPrimary && Number(current.kitId) > 0) {
      // Pass the primary's productId so only ITS kit rows sync — a kit shared by several primaries
      // calculates independently per primary (same as the manual-estimate grid).
      const synced = syncKitAddonQuantity(
        updatedMats, labs, current.kitId, val, current.unit, current.productId
      );
      return { ...item, material: synced.material, labor: synced.labor };
    }

    if (
      !current?.isPrimary &&
      Number(current?.kitId) > 0 &&
      String(current?.unit ?? "").trim().toUpperCase() === "EA" &&
      Number(current?.coverage) > 0
    ) {
      // Resolve the edited addon's OWN primary (by parentProductId when present) so the cascade
      // stays within this primary's kit rows.
      const primaryItem = updatedMats.find((m) =>
        m.isPrimary &&
        (current.parentProductId
          ? Number(m.productId) === Number(current.parentProductId)
          : Number(m.kitId) === Number(current.kitId))
      );
      const isSFMaster = String(primaryItem?.unit ?? "").trim().toUpperCase() === "SF";
      if (!isSFMaster) {
        const synced = syncDerivedFromEaAddon(
          updatedMats, labs, current.kitId, val, current.parentProductId
        );
        return { ...item, material: synced.material, labor: synced.labor };
      }
    }

    return { ...item, material: updatedMats };
  };

  // onChange — update value immediately; for LF primary kits, skip addon sync until blur.
  const handleQtyChange = (itemIdx, section, lineIdx, value) => {
    const val = clampQty(value);
    const isLFPrimary = (() => {
      if (section !== "material") return false;
      const current = (localOrderItems[itemIdx]?.material ?? [])[lineIdx];
      return current?.isPrimary &&
        Number(current.kitId) > 0 &&
        String(current.unit ?? "").trim().toUpperCase() === "LF";
    })();
    setLocalOrderItems(prev => prev.map((item, i) =>
      i === itemIdx ? applyQtyToItem(item, section, lineIdx, val, !isLFPrimary) : item
    ));
  };

  // onBlur — trigger kit sync for LF primary kits after the user commits the value.
  const handleQtyBlur = (itemIdx, section, lineIdx, value) => {
    if (section !== "material") return;
    const current = (localOrderItems[itemIdx]?.material ?? [])[lineIdx];
    if (!(current?.isPrimary && Number(current.kitId) > 0 &&
      String(current.unit ?? "").trim().toUpperCase() === "LF")) return;
    const val = clampQty(value);
    setLocalOrderItems(prev => prev.map((item, i) =>
      i === itemIdx ? applyQtyToItem(item, section, lineIdx, val, true) : item
    ));
  };

  const handleDeleteMaterial = (itemIdx, lineIdx) =>
    setLocalOrderItems((prev) => prev.map((item, i) =>
      i === itemIdx
        ? { ...item, material: (item.material ?? []).filter((_, j) => j !== lineIdx) }
        : item
    ));

  const handleDeleteLabor = (itemIdx, lineIdx) =>
    setLocalOrderItems((prev) => prev.map((item, i) =>
      i === itemIdx
        ? { ...item, labor: (item.labor ?? []).filter((_, j) => j !== lineIdx) }
        : item
    ));

  const handleUpdateOrder = () => {
    const errs = {};
    if (!measureDate) errs.measureDate = "Measure Date is required.";
    else if (measureDate < today) errs.measureDate = "Measure Date cannot be in the past.";
    if (!pdfFile && !d?.MeasurePDF) errs.pdfFile = "Measure PDF is required.";
    if (Object.keys(errs).length) {
      setEditErrors(errs);
      return;
    }
    setEditErrors({});
    const formData = new FormData();
    formData.append("JobNo", d?.jobNo ?? "");
    formData.append("OrderPDF", "");
    formData.append("ClientPDF", "");
    formData.append("uniqueChannelName", auth?.[0]?.uniqueChannelName ?? "");
    formData.append("ContactInfoId", contactInfoId ?? "");
    formData.append("unitTypeId", d?.unitTypeId ?? "");
    formData.append("cnCMeasureQuantityId", d?.cnCMeasureQuantityId ?? 0);
    formData.append("floorplan", d?.floorplan ?? "");
    formData.append("orderItems", JSON.stringify(localOrderItems));
    formData.append("InstallationData", JSON.stringify({
      Occupaied: d?.InstallationData?.Occupaied ?? "No",
      PurchaseOrder: d?.InstallationData?.PurchaseOrder ?? "",
      Instruction: d?.InstallationData?.Instruction ?? "",
      Email: d?.InstallationData?.Email ?? "",
      PhoneNo: d?.InstallationData?.PhoneNo ?? "",
      MeasureDate: measureDate,
    }));
    formData.append("CreatedBy", auth?.[0]?.userId ?? "");
    formData.append("unitId", d?.unitId ?? "");
    formData.append("UnitName", d?.UnitName ?? "");
    formData.append("UnitType", d?.UnitType ?? "");
    formData.append("SalesPersonId", "");
    formData.append("MeasurePDF", pdfFile ?? d?.MeasurePDF ?? "");
    console.log('--pdfFile--', pdfFile)
    updateCncOrder(auth[0].token, formData);
  };

  const handleAddNewItems = (merged) => {
    const scopeStructureId = (line) => Number(line.scopeProductStructureId ?? line.productStructureId);
    setLocalOrderItems((prev) => {
      const updated = prev.map((orderItem) => {
        const newMats = (merged.material ?? []).filter(
          (m) => scopeStructureId(m) === Number(orderItem.productStructureId)
        );
        const newLabs = (merged.labor ?? []).filter(
          (l) => scopeStructureId(l) === Number(orderItem.productStructureId)
        );
        if (!newMats.length && !newLabs.length) return orderItem;
        return {
          ...orderItem,
          material: [...(orderItem.material ?? []), ...newMats],
          labor: [...(orderItem.labor ?? []), ...newLabs],
        };
      });

      // Handle structures not yet in localOrderItems
      const existingIds = new Set(prev.map((oi) => Number(oi.productStructureId)));
      const newStructureIds = [...new Set([
        ...(merged.material ?? []).map(scopeStructureId),
        ...(merged.labor ?? []).map(scopeStructureId),
      ])].filter((id) => !existingIds.has(id));

      newStructureIds.forEach((structId) => {
        const newMats = (merged.material ?? []).filter((m) => scopeStructureId(m) === structId);
        const newLabs = (merged.labor ?? []).filter((l) => scopeStructureId(l) === structId);
        const structName = structureNames[structId] || (newMats[0] ?? newLabs[0])?.structureName || "";
        updated.push({
          productStructureId: structId,
          productStructureName: structName,
          material: newMats,
          labor: newLabs,
          roomName: "",
          photoUrl: "",
        });
      });

      return updated;
    });
    setAddScopeOpen(false);
  };

  // Added rows join the target scope block (this orderItem) but keep their OWN product structure —
  // a scope block can hold several structures. Keeping productStructureId/structureName = the row's
  // real structure makes the "Product Structure" column correct and records the right structureId.
  const stampLineForItem = (orderItem) => (line) => ({
    ...line,
    productStructureId:
      Number(line.productStructureId ?? 0) || Number(orderItem?.productStructureId) || 0,
    structureName: line.structureName || orderItem?.productStructureName || "",
  });

  const handleAddMaterialToItem = ({ material = [], labor = [] } = {}) => {
    const idx = addMaterialTargetIdx;
    if (idx != null) {
      setLocalOrderItems((prev) =>
        prev.map((oi, i) => {
          if (i !== idx) return oi;
          const stamp = stampLineForItem(oi);
          return {
            ...oi,
            material: [...(oi.material ?? []), ...material.map(stamp)],
            labor: [...(oi.labor ?? []), ...labor.map(stamp)],
          };
        })
      );
    }
    setAddMaterialTargetIdx(null);
  };

  const handleAddLaborToItem = (labor = []) => {
    const idx = addLaborTargetIdx;
    if (idx != null && labor.length) {
      setLocalOrderItems((prev) =>
        prev.map((oi, i) => {
          if (i !== idx) return oi;
          const stamp = stampLineForItem(oi);
          return { ...oi, labor: [...(oi.labor ?? []), ...labor.map(stamp)] };
        })
      );
    }
    setAddLaborTargetIdx(null);
  };

  return (
    <MainWrapper>
      {isLoading && (
        <div className="loading-container"><div className="spinner-border"></div></div>
      )}
      <div className="container">
        <div className="sub-header mt-5 mb-3">
          <Link className="btn btn-primary me-1" to={isReadOnlyView ? "/existing-orders" : "/cnc-orders"}>
            <Icon icon={ic_arrow_back} />Back
          </Link>
        </div>

        <Card className="mb-4">
          <Card.Body>
            {/* Action buttons */}
            <div className="make-changes text-end mb-4">
              {isEditMode && !isReadOnlyView && (
                <Button
                  className="me-2"
                  style={{ backgroundColor: "#2D831F", borderColor: "#2D831F" }}
                  onClick={handleUpdateOrder}
                  disabled={isLoading}
                >
                  {isLoading ? "Updating…" : "Update Order"}
                </Button>
              )}
              {!isReadOnlyView && (
                <button
                  className="btn btn-success me-2"
                  disabled={isLoading}
                  onClick={() => setShowConfirmModal(true)}
                >
                  Convert to Order
                </button>
              )}
              {!isReadOnlyView && (
                <button
                  className="btn btn-primary me-2"
                  onClick={() => {
                    setIsEditMode((prev) => {
                      if (!prev) setMeasureDate(d?.InstallationData?.MeasureDate ?? "");
                      else {
                        // Cancel: discard unsaved local changes (deletes / qty edits).
                        setMeasureDate("");
                        setPdfFile(null);
                        setLocalOrderItems(mapOrderItems(cncOrderDetail));
                      }
                      setEditErrors({});
                      return !prev;
                    });
                  }}
                >
                  {isEditMode ? "Cancel" : "Edit Order"}
                </button>
              )}
              <button className="btn btn-primary" onClick={() => setShowDeleteModal(true)}>Delete Order</button>
            </div>

            <Row className="justify-content-center">
              <Col md={9}>
                <div className="order-detail px-0">
                  <h3 className="mb-4">Measure Details</h3>

                  <Form.Group className="row mb-2">
                    <Form.Label className="col-md-4">Property Name</Form.Label>
                    <span className="value col-md-8">{c?.shipToPropertyName}</span>
                  </Form.Group>
                  <Form.Group className="row mb-2">
                    <Form.Label className="col-md-4">Address</Form.Label>
                    <span className="value col-md-8">{c?.shipToAddress}</span>
                  </Form.Group>
                  <Form.Group className="row mb-2">
                    <Form.Label className="col-md-4">City</Form.Label>
                    <span className="value col-md-8">{c?.shipToCity}</span>
                  </Form.Group>
                  <Form.Group className="row mb-2">
                    <Form.Label className="col-md-4">State</Form.Label>
                    <span className="value col-md-8">{c?.shipToState}</span>
                  </Form.Group>
                  <Form.Group className="row mb-2">
                    <Form.Label className="col-md-4">Zip</Form.Label>
                    <span className="value col-md-8">{c?.shipToZip}</span>
                  </Form.Group>
                  <Form.Group className="row mb-2">
                    <Form.Label className="col-md-4">Purchase Order</Form.Label>
                    <span className="value col-md-8">{d?.InstallationData?.PurchaseOrder ?? firstContact?.PurchaseOrder}</span>
                  </Form.Group>
                  <Form.Group className="row mb-2">
                    <Form.Label className="col-md-4">Requestor Email</Form.Label>
                    <span className="value col-md-8">{d?.InstallationData?.Email ?? firstContact?.email}</span>
                  </Form.Group>
                  <Form.Group className="row mb-2">
                    <Form.Label className="col-md-4">Requestor Phone Number</Form.Label>
                    <span className="value col-md-8">{d?.InstallationData?.PhoneNo ?? firstContact?.phone}</span>
                  </Form.Group>

                  {/* Installation dates — one per product structure */}
                  {orderItems.filter(it => it.InstallationDate).map((it, i) => (
                    <Form.Group key={i} className="row mb-2">
                      <Form.Label className="col-md-4">
                        Installation Date ({it.productStructureName ?? it.ProductStructureName ?? ''})
                      </Form.Label>
                      <span className="value col-md-8">{it.InstallationDate}</span>
                    </Form.Group>
                  ))}

                  <Form.Group className="row mb-2">
                    <Form.Label className="col-md-4">Unit#</Form.Label>
                    <div className="col-md-8">{d?.UnitName ?? ''}</div>
                  </Form.Group>
                  <Form.Group className="row mb-2">
                    <Form.Label className="col-md-4">Unit Type</Form.Label>
                    <div className="col-md-8">{d?.UnitType ?? d?.unitTypeName ?? ''}</div>
                  </Form.Group>
                  <Form.Group className="row mb-2">
                    <Form.Label className="col-md-4">Order Date &amp; Time</Form.Label>
                    <span className="value col-md-8">{d?.createdOn}</span>
                  </Form.Group>
                  <Form.Group className="row mb-2">
                    <Form.Label className="col-md-4">Order Number</Form.Label>
                    <span className="value col-md-8">{d?.jobNo}</span>
                  </Form.Group>
                  <Form.Group className="row mb-2">
                    <Form.Label className="col-md-4">Special Instructions</Form.Label>
                    <span className="value col-md-8">{d?.InstallationData?.Instruction ?? firstContact?.Instruction}</span>
                  </Form.Group>

                  {d?.InstallationData?.MeasureDate && !isEditMode && (
                    <Form.Group className="row mb-2">
                      <Form.Label className="col-md-4">Measure Date</Form.Label>
                      <span className="value col-md-8">{d.InstallationData.MeasureDate}</span>
                    </Form.Group>
                  )}

                  {d?.MeasurePDF && (
                    <Form.Group className="row mb-2">
                      <Form.Label className="col-md-4">Measure PDF</Form.Label>
                      <div className="col-md-8">
                        <a href={d.MeasurePDF} target="_blank" rel="noopener noreferrer">
                          View PDF
                        </a>
                      </div>
                    </Form.Group>
                  )}

                  {isEditMode && (
                    <>
                      <Form.Group className="row mb-2">
                        <Form.Label className="col-md-4">
                          Measure Date <span className="text-danger">*</span>
                        </Form.Label>
                        <div className="col-md-8">
                          <Form.Control
                            type="date"
                            min={today}
                            value={measureDate}
                            isInvalid={!!editErrors.measureDate}
                            onChange={(e) => {
                              setMeasureDate(e.target.value);
                              setEditErrors((prev) => ({ ...prev, measureDate: "" }));
                            }}
                          />
                          <Form.Control.Feedback type="invalid">
                            {editErrors.measureDate}
                          </Form.Control.Feedback>
                        </div>
                      </Form.Group>
                      <Form.Group className="row mb-2">
                        <Form.Label className="col-md-4">
                          Upload Measure PDF <span className="text-danger">*</span>
                        </Form.Label>
                        <div className="col-md-8">
                          <Form.Control
                            type="file"
                            accept=".pdf"
                            isInvalid={!!editErrors.pdfFile}
                            onChange={(e) => {
                              setPdfFile(e.target.files?.[0] || null);
                              setEditErrors((prev) => ({ ...prev, pdfFile: "" }));
                            }}
                          />
                          <Form.Control.Feedback type="invalid">
                            {editErrors.pdfFile}
                          </Form.Control.Feedback>
                          {pdfFile && (
                            <div className="text-muted small mt-1">{pdfFile.name}</div>
                          )}
                        </div>
                      </Form.Group>
                    </>
                  )}
                </div>
              </Col>
            </Row>

            {/* Floor plan */}
            {d?.floorplan && (
              <div className="mb-4 text-center">
                <img src={d.floorplan} alt="Floor Plan" style={{ maxWidth: '60%' }} />
              </div>
            )}

            {/* Product structure cards */}
            {localOrderItems.map((item, idx) => {
              const mats = item.material ?? [];
              const labs = item.labor ?? [];
              const { cost: mCost, profit: mProfit, gpm: mGPM, total: mTotal } = calcSection(mats);
              const { cost: lCost, profit: lProfit, gpm: lGPM, total: lTotal } = calcSection(labs);
              const sectionTotal = item.totalPrice ?? (mTotal + lTotal);
              const mUnitTotal = mats.reduce((s, x) => s + parseFloat(x.unitPrice ?? x.price ?? 0), 0);
              const lUnitTotal = labs.reduce((s, x) => s + parseFloat(x.unitPrice ?? x.price ?? 0), 0);
              console.log('--mats--', item)

              return (
                <div key={idx} className="border rounded p-3 mb-4">
                  {/* Section header */}
                  <div className="d-flex align-items-start justify-content-between mb-3 flex-wrap gap-2">
                    <div className="d-flex align-items-center gap-3 flex-wrap">
                      <h5 style={{ color: '#28a745', marginBottom: 0 }}>{item.productStructureName}</h5>
                      <div className="d-flex align-items-center gap-2">
                        <Form.Label className="mb-0 text-nowrap" style={{ color: '#c07000' }}>Room Name</Form.Label>
                        <Form.Control size="sm" readOnly value={item.roomName ?? ''} style={{ width: 160 }} />
                      </div>
                      {item?.photoUrl && (
                        <div className="d-flex flex-column align-items-center">
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 36px)', gap: 2 }}>
                            <img
                              key={item.productStructureName}
                              src={item.photoUrl}
                              alt=""
                              style={{ width: 36, height: 36, objectFit: 'cover', border: '1px solid #ccc', cursor: 'pointer' }}
                              onClick={() => setPreviewPhoto(item.photoUrl)}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    <strong>{item.productStructureName} Total: ${fmt(sectionTotal)}</strong>
                  </div>

                  {/* Material table */}
                  <h6 className="mb-2">Material</h6>
                  <div className="fixTableHead mb-0">
                    <Table bordered size="sm" className="mb-0">
                      <thead>
                        <tr>
                          <th>Qty</th>
                          <th>Unit</th>
                          <th>Private Name</th>
                          <th>Style</th>
                          <th>Color</th>
                          <th>Product Structure</th>
                          <th>Unit Price</th>
                          <th>Line Price</th>
                          {isEditMode ? <th style={{ width: "70px" }}>Actions</th> : null}
                        </tr>
                      </thead>
                      <tbody>
                        {mats.map((m, mi) => (
                          <tr key={mi}>
                            <td width="90">
                              <Form.Control
                                size="sm"
                                type="number"
                                min="0"
                                readOnly={!isEditMode}
                                value={m.quantity ?? ''}
                                onChange={(e) => handleQtyChange(idx, 'material', mi, e.target.value)}
                                onBlur={(e) => handleQtyBlur(idx, 'material', mi, e.target.value)}
                              />
                            </td>
                            <td>{m.unitName ?? m.unit ?? ''}</td>
                            <td>
                              {m.privateName ?? m.pvtName ?? ''}
                              {!m.isPrimary && m.parentProductName ? (
                                <div className="text-muted small fst-italic">for {m.parentProductName}</div>
                              ) : null}
                            </td>
                            <td>{m.style ?? m.product ?? ''}</td>
                            <td>{m.color ?? ''}</td>
                            <td>{getOrderLineStructureName(m, item, structureNames)}</td>
                            <td width="90"><Form.Control size="sm" readOnly value={m.unitPrice ?? m.price ?? ''} /></td>
                            <td width="90"><Form.Control size="sm" readOnly value={m.linePrice ?? ''} /></td>
                            {isEditMode ? (
                              <td className="text-center align-middle">
                                <Button
                                  variant="link"
                                  type="button"
                                  className="text-danger p-0"
                                  aria-label={`Delete material ${mi + 1}`}
                                  onClick={() => handleDeleteMaterial(idx, mi)}
                                >
                                  <Icon size={20} icon={ic_delete_outline} />
                                </Button>
                              </td>
                            ) : null}
                          </tr>
                        ))}
                        <tr>
                          <td colSpan={6} align="right" className="border-0">
                            <div className="form-control border-0 bg-transparent py-0">Total</div>
                          </td>
                          <td><div className="form-control border-0 bg-transparent py-0">${fmt(mUnitTotal)}</div></td>
                          <td><div className="form-control border-0 bg-transparent py-0">${fmt(mTotal)}</div></td>
                          {isEditMode ? <td className="border-0"></td> : null}
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                  <Table className="mb-0">
                    <tbody>
                      <tr>
                        <td style={{ backgroundColor: '#f2f2f2' }} align="right">
                          <strong>Material Cost: ${fmt(mCost)}</strong>
                        </td>
                        <td style={{ backgroundColor: '#f2f2f2' }} align="right">
                          <strong>Material Profit: ${fmt(mProfit)}</strong>
                        </td>
                        <td style={{ backgroundColor: '#f2f2f2' }} align="right">
                          <strong>Material GPM: {fmt(mGPM)}%</strong>
                        </td>
                      </tr>
                    </tbody>
                  </Table>

                  {isEditMode ? (
                    <div className="text-center mb-3">
                      <Button variant="success" onClick={() => setAddMaterialTargetIdx(idx)}>
                        Add New Material Item
                      </Button>
                    </div>
                  ) : null}

                  <hr />

                  {/* Labor table */}
                  <h6 className="mb-2">Labor</h6>
                  <div className="fixTableHead mb-0">
                    <Table bordered size="sm" className="mb-0">
                      <thead>
                        <tr>
                          <th>Qty</th>
                          <th>Unit</th>
                          <th>Labor Category</th>
                          <th>Labor Name</th>
                          <th>Unit Price</th>
                          <th>Line Price</th>
                          {isEditMode ? <th style={{ width: "70px" }}>Actions</th> : null}
                        </tr>
                      </thead>
                      <tbody>
                        {labs.map((l, li) => (
                          <tr key={li}>
                            <td width="90">
                              <Form.Control
                                size="sm"
                                type="number"
                                min="0"
                                readOnly={!isEditMode}
                                value={l.quantity ?? ''}
                                onChange={(e) => handleQtyChange(idx, 'labor', li, e.target.value)}
                              />
                            </td>
                            <td>{l.unitName ?? l.unit ?? ''}</td>
                            <td>{l.laborCategory ?? ''}</td>
                            <td>
                              {l.laborName ?? l.labor ?? ''}
                              {l.parentProductName ? (
                                <div className="text-muted small fst-italic">for {l.parentProductName}</div>
                              ) : null}
                            </td>
                            <td width="90"><Form.Control size="sm" readOnly value={l.unitPrice ?? l.price ?? ''} /></td>
                            <td width="90"><Form.Control size="sm" readOnly value={l.linePrice ?? ''} /></td>
                            {isEditMode ? (
                              <td className="text-center align-middle">
                                <Button
                                  variant="link"
                                  type="button"
                                  className="text-danger p-0"
                                  aria-label={`Delete labor ${li + 1}`}
                                  onClick={() => handleDeleteLabor(idx, li)}
                                >
                                  <Icon size={20} icon={ic_delete_outline} />
                                </Button>
                              </td>
                            ) : null}
                          </tr>
                        ))}
                        <tr>
                          <td colSpan={4} align="right" className="border-0">
                            <div className="form-control border-0 bg-transparent py-0">Total</div>
                          </td>
                          <td><div className="form-control border-0 bg-transparent py-0">${fmt(lUnitTotal)}</div></td>
                          <td><div className="form-control border-0 bg-transparent py-0">${fmt(lTotal)}</div></td>
                          {isEditMode ? <td className="border-0"></td> : null}
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                  <Table className="mb-0">
                    <tbody>
                      <tr>
                        <td style={{ backgroundColor: '#f2f2f2' }} align="right">
                          <strong>Labor Cost: ${fmt(lCost)}</strong>
                        </td>
                        <td style={{ backgroundColor: '#f2f2f2' }} align="right">
                          <strong>Labor Profit: ${fmt(lProfit)}</strong>
                        </td>
                        <td style={{ backgroundColor: '#f2f2f2' }} align="right">
                          <strong>Labor GPM: {fmt(lGPM)}%</strong>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                  {isEditMode ? (
                    <div className="text-center mt-3">
                      <Button variant="success" onClick={() => setAddLaborTargetIdx(idx)}>
                        Add New Labor Item
                      </Button>
                    </div>
                  ) : null}
                </div>
              );
            })}
            {/* Estimate Total */}
            {orderItems.length > 0 && (
              <div className="text-end mb-2">
                <strong>Estimate Total: ${fmt(estimateTotal)}</strong>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Photo preview modal */}
        <Modal show={!!previewPhoto} onHide={() => setPreviewPhoto(null)} centered size="lg">
          <Modal.Header closeButton />
          <Modal.Body className="text-center p-2">
            {previewPhoto && (
              <img src={previewPhoto} alt="Preview" style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }} />
            )}
          </Modal.Body>
        </Modal>

        {/* Convert to Order confirmation modal */}
        <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Convert to Order</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Are you sure you want to convert order <strong>{d?.jobNo}</strong> to a confirmed order?
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>Cancel</Button>
            <Button
              variant="success"
              disabled={isLoading}
              onClick={() => {
                setShowConfirmModal(false);
                confirmCncOrder(auth[0].token, d?.jobNo, auth[0].userId);
              }}
            >
              Confirm
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Delete confirmation modal */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Delete Order</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Are you sure you want to delete order <strong>{d?.jobNo}</strong>? This action cannot be undone.
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
            <Button
              variant="danger"
              onClick={() => {
                setShowDeleteModal(false);
                deleteCncOrder(auth[0].token, d?.cnCOrderDetailsId, d?.jobNo);
              }}
            >
              Delete
            </Button>
          </Modal.Footer>
        </Modal>
        <AddScopeModal
          show={addScopeOpen}
          onClose={() => setAddScopeOpen(false)}
          onAdd={handleAddNewItems}
          storeId={c?.storeId ?? 0}
          contactInfoId={contactInfoId}
          initialCheckedStructures={localOrderItems.map((oi) => oi.productStructureId)}
          lockedStructureIds={localOrderItems.map((oi) => oi.productStructureId)}
        />
        {addMaterialTargetIdx != null ? (
          <AddScopeModal
            show={addMaterialTargetIdx != null}
            onClose={() => setAddMaterialTargetIdx(null)}
            onAdd={handleAddMaterialToItem}
            storeId={c?.storeId ?? 0}
            contactInfoId={contactInfoId}
            initialCheckedStructures={[localOrderItems[addMaterialTargetIdx]?.productStructureId]}
          />
        ) : null}
        {addLaborTargetIdx != null ? (
          <AddLaborItemModal
            show={addLaborTargetIdx != null}
            onClose={() => setAddLaborTargetIdx(null)}
            onAdd={handleAddLaborToItem}
            storeId={c?.storeId ?? 0}
            contactInfoId={contactInfoId}
            structureName={localOrderItems[addLaborTargetIdx]?.productStructureName}
          />
        ) : null}
      </div>
    </MainWrapper>
  );
};

const mapStateToProps = ({ auth, customerInfo, ProductType }) => ({ auth, customerInfo, ProductType });

const mapDispatchToProps = (dispatch) => ({
  fetchProductType: (token) => dispatch({ type: PRODUCT_TYPE_REQUEST, token }),
  fetchCncOrderDetail: (token, jobNo) =>
    dispatch({ type: CNC_ORDER_DETAIL_REQUEST, token, jobNo }),
  fetchCncCustomerInfo: (token, userId, contactInfoId) =>
    dispatch({ type: CNC_CUSTOMER_INFO_REQUEST, token, userId, contactInfoId }),
  deleteCncOrder: (token, cncOrderDetailsId, jobNo) =>
    dispatch({ type: CNC_ORDER_DELETE_REQUEST, token, cncOrderDetailsId, jobNo }),
  updateCncOrder: (token, payload) =>
    dispatch({ type: CNC_ORDER_UPDATE_REQUEST, token, payload }),
  confirmCncOrder: (token, jobNo, confirmedBy) =>
    dispatch({ type: CNC_ORDER_CONFIRM_REQUEST, token, jobNo, confirmedBy }),
});

export default connect(mapStateToProps, mapDispatchToProps)(CncOrderDetail);
